
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { fetchCVVersions, getCVVersionUrl } from "@/utils/candidate/cv-storage";
import { CVFullScreenModal } from "./CVFullScreenModal";
import { toast } from "@/hooks/use-toast";
import { getFormattedDateTime } from "@/utils/settings";

interface CVVersionHistoryProps {
  candidateId: string;
  isVisible: boolean;
  onVersionSelect?: (showVersions: boolean) => void;
}

export const CVVersionHistory = ({ 
  candidateId, 
  isVisible,
  onVersionSelect
}: CVVersionHistoryProps) => {
  const [versions, setVersions] = useState<Array<{name: string, created_at: string}>>([]);
  const [viewingCVUrl, setViewingCVUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  useEffect(() => {
    if (isVisible && candidateId) {
      loadVersions();
    }
  }, [isVisible, candidateId]);
  
  const loadVersions = async () => {

    setIsLoading(true);
    try {

      const versionsData = await fetchCVVersions(candidateId);
      console.log("handleViewCVhandleViewCV", versionsData)
      setVersions(versionsData);
      
    } catch (error) {
      console.error("Error loading CV versions:", error);
      toast({
        title: "Error",
        description: "Failed to load CV version history",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const viewVersion = async (versionName: string) => {
    try {
      const url = await getCVVersionUrl(candidateId, versionName);
      if (url) {
        setViewingCVUrl(url);
      } else {
        toast({
          title: "Error",
          description: "Failed to retrieve CV version",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error viewing CV version:", error);
      toast({
        title: "Error",
        description: "Failed to view this CV version",
        variant: "destructive"
      });
    }
  };
  
  if (!isVisible) {
    return null;
  }
  
  return (
    <>
      <div className="border border-gray-200 rounded-lg mb-3 p-2 bg-gray-50">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-sm font-semibold">Previous Versions</h4>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onVersionSelect?.(false)}
            className="h-6 text-xs"
          >
            Close
          </Button>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center py-4">
            <div className="animate-pulse h-4 bg-gray-200 rounded w-32"></div>
          </div>
        ) : versions.length > 0 ? (
          <div className="max-h-32 overflow-y-auto">
            <ul className="space-y-1">
              {versions.map((version, index) => {

                const timestamp = version.created_at;
                const date = new Date(timestamp);
                // Use the new formatting function
                const formattedDate = getFormattedDateTime(date);
                
                return (
                  <li key={index} className="text-xs flex justify-between items-center py-1 border-b border-gray-100 last:border-0">
                    <div>
                      <span className="font-medium">{formattedDate}</span>
                    </div>
                    <Button variant="link" size="sm" className="h-5 p-0" onClick={() => viewVersion(version.name)}>
                      View {version.name}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <div className="text-center py-2 text-sm text-gray-500">
            No previous versions found
          </div>
        )}
      </div>
      
      {/* Full Screen CV Modal for history versions */}
      {viewingCVUrl && (
        <CVFullScreenModal
          cvUrl={viewingCVUrl}
          onClose={() => setViewingCVUrl(null)}
        />
      )}
    </>
  );
};
